import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Button } from "./ui/button";
import { cn } from "./ui/utils";

type FavoritesData = { favorite_ips: string[] };

export function FavoriteStar({ ipId, isFavorite }: { ipId: string; isFavorite: boolean }) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ add }: { add: boolean }) =>
      add ? api.addFavorite(ipId) : api.removeFavorite(ipId),
    onMutate: async ({ add }) => {
      await qc.cancelQueries({ queryKey: queryKeys.favorites });
      const prev = qc.getQueryData<FavoritesData>(queryKeys.favorites);
      qc.setQueryData<FavoritesData>(queryKeys.favorites, (old) => ({
        favorite_ips: add
          ? [...(old?.favorite_ips || []), ipId]
          : (old?.favorite_ips || []).filter((x) => x !== ipId),
      }));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.favorites, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: queryKeys.favorites }),
  });

  return (
    <Button
      variant="ghost"
      size="icon"
      aria-pressed={isFavorite}
      aria-label={isFavorite ? "お気に入りを解除" : "お気に入りに追加"}
      onClick={(e) => {
        e.stopPropagation();
        mutation.mutate({ add: !isFavorite });
      }}
    >
      <Star className={cn("h-4 w-4", isFavorite ? "fill-yellow-400 text-yellow-500" : "text-muted-foreground")} />
    </Button>
  );
}
